import { CurrencyAmount, JSBI, Token, TokenAmount, Trade } from '@agent13/sdk-rei'
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { ArrowDown } from 'react-feather'
import { CardBody, ArrowDownIcon, Button, IconButton, Text } from '../../uikit-foodcourt'
import { ThemeContext } from 'styled-components'
import AddressInputPanel from 'components/AddressInputPanel'
import Card, { GreyCard } from 'components/Card'
import { AutoColumn } from 'components/Column'
import ConfirmSwapModal from 'components/swap/ConfirmSwapModal'
import CurrencyInputPanel from 'components/CurrencyInputPanel'
import CardNav from 'components/CardNav'
import { AutoRow, RowBetween } from 'components/Row'
import AdvancedSwapDetailsDropdown from 'components/swap/AdvancedSwapDetailsDropdown'
import confirmPriceImpactWithoutFee from 'components/swap/confirmPriceImpactWithoutFee'
import { ArrowWrapper, BottomGrouping, SwapCallbackError, Wrapper } from 'components/swap/styleds'
import TradePrice from 'components/swap/TradePrice'
import TokenWarningModal from 'components/TokenWarningModal'
import SyrupWarningModal from 'components/SyrupWarningModal'
import SafeMoonWarningModal from 'components/SafeMoonWarningModal'
import RSafeWarningModal from 'components/RSafeWarningModal'
import ProgressSteps from 'components/ProgressSteps'
import Container from 'components/Container'

import { INITIAL_ALLOWED_SLIPPAGE } from 'constants/index'
import { useActiveWeb3React } from 'hooks'
import {
	Safe2RSafe,
	Safe2WSafe,
	tokenInfo,
	useCurrency,
	WSafe2RSafe,
	WSafe2Safe,
} from 'hooks/Tokens'
import {
	ApprovalState,
	useApproveCallback,
	useApproveCallbackFromTrade,
} from 'hooks/useApproveCallback'
import { useSwapCallback } from 'hooks/useSwapCallback'
import useWrapCallback, { WrapType } from 'hooks/useWrapCallback'
import { Field } from 'state/swap/actions'
import {
	useDefaultsFromURLSearch,
	useDerivedSwapInfo,
	useSwapActionHandlers,
	useSwapState,
} from 'state/swap/hooks'
import { useExpertModeManager, useUserDeadline, useUserSlippageTolerance } from 'state/user/hooks'
import { LinkStyledButton } from 'components/Shared'
import { maxAmountSpend } from 'utils/maxAmountSpend'
import { computeTradePriceBreakdown, warningSeverity } from 'utils/prices'
import Loader from 'components/Loader'
import useI18n from 'hooks/useI18n'
import PageHeader from 'components/PageHeader'
import ConnectWalletButton from 'components/ConnectWalletButton'
import AppBody from '../AppBody'
import { WrappedTokenInfo } from 'state/lists/hooks'
import { SAFEMOON_TOKEN_ADDRESS, WSAFEMOON_TOKEN_ADDRESS } from 'constants/lists'
import { BigNumber, ethers } from 'ethers'
import { useWSafeTokenContract } from 'hooks/useContract'
import { useSingleCallResult } from 'state/multicall/hooks'

const Wrap = () => {
	const loadedUrlParams = useDefaultsFromURLSearch()
	const TranslateString = useI18n()

	// token warning stuff
	const [loadedInputCurrency, loadedOutputCurrency] = [
		useCurrency(loadedUrlParams?.inputCurrencyId),
		useCurrency(loadedUrlParams?.outputCurrencyId),
	]
	const [dismissTokenWarning, setDismissTokenWarning] = useState<boolean>(false)
	const [rSafeTokenWarning, setRSafeTokenWarning] = useState<boolean>(true)
	const [transactionWarning, setTransactionWarning] = useState<{
		selectedToken: string | null
		purchaseType: string | null
	}>({
		selectedToken: null,
		purchaseType: null,
	})
	const urlLoadedTokens: Token[] = useMemo(
		() =>
			[loadedInputCurrency, loadedOutputCurrency]?.filter(
				(c): c is Token => c instanceof Token,
			) ?? [],
		[loadedInputCurrency, loadedOutputCurrency],
	)
	const handleConfirmTokenWarning = useCallback(() => {
		setDismissTokenWarning(true)
	}, [])
	const handleConfirmRSafeTokenWarning = useCallback(() => {
		setRSafeTokenWarning(false)
	}, [])

	const handleConfirmWarning = () => {
		setTransactionWarning({
			selectedToken: null,
			purchaseType: null,
		})
	}

	const { account, chainId } = useActiveWeb3React()
	const theme = useContext(ThemeContext)

	const [isExpertMode] = useExpertModeManager()

	// get custom setting values for user
	const [deadline] = useUserDeadline()
	const [allowedSlippage] = useUserSlippageTolerance()

	const [inputTokenAddress, setInputTokenAddress] = useState('')
	const [outputTokenAddress, setOutputTokenAddress] = useState('')

	const [wSafeTokenAddress, setWSafeTokenAddress] = useState('')
	const wSafeContract = useWSafeTokenContract(
		wSafeTokenAddress || WSAFEMOON_TOKEN_ADDRESS[chainId],
		false,
	)
	const wrapRatio =
		parseFloat(
			(
				useSingleCallResult(wSafeContract, 'getWrapRatio', []).result ?? 1000000000
			).toString(),
		) / 1000000000

	// console.log(wrapRatio)

	const [inputAmount, setInputAmount] = useState('')
	const [inputPairAmount, setInputPairAmount] = useState('')

	const [outputAmount, setOutputAmount] = useState('')
	const [outputPairAmount, setOutputPairAmount] = useState('')

	useEffect(() => {
		const inputToken = tokenInfo(inputTokenAddress)
		const outputToken = tokenInfo(outputTokenAddress)
		let wSafeToken

		if (
			inputToken &&
			outputToken &&
			(inputToken.tokenInfo.isWSafeToken || outputToken.tokenInfo.isWSafeToken)
		) {
			if (inputToken.tokenInfo.isWSafeToken) {
				wSafeToken = inputToken
			} else {
				wSafeToken = outputToken
			}

			setWSafeTokenAddress(wSafeToken.address)
		}
	}, [inputTokenAddress, outputTokenAddress, inputAmount, outputAmount])

	// swap state
	const { independentField, typedValue, recipient } = useSwapState()
	const {
		v2Trade,
		currencyBalances,
		parsedAmount,
		currencies,
		inputError: swapInputError,
	} = useDerivedSwapInfo()
	const { wrapType, execute: onWrap, inputError: wrapInputError } = useWrapCallback(
		currencies[Field.INPUT],
		currencies[Field.OUTPUT],
		typedValue,
		inputTokenAddress,
		outputTokenAddress,
		inputAmount,
	)
	const showWrap: boolean = wrapType !== WrapType.NOT_APPLICABLE
	const trade = showWrap ? undefined : v2Trade

	// console.log(showWrap)

	// console.log(currencies)

	const parsedAmounts = showWrap
		? {
				[Field.INPUT]: parsedAmount,
				[Field.OUTPUT]: parsedAmount,
		  }
		: {
				[Field.INPUT]: independentField === Field.INPUT ? parsedAmount : trade?.inputAmount,
				[Field.OUTPUT]:
					independentField === Field.OUTPUT ? parsedAmount : trade?.outputAmount,
		  }

	const {
		onSwitchTokens,
		onCurrencySelection,
		onUserInput,
		onChangeRecipient,
	} = useSwapActionHandlers()
	const isValid = !swapInputError
	const dependentField: Field = independentField === Field.INPUT ? Field.OUTPUT : Field.INPUT

	function onSwitchTokensWrapped() {
		onSwitchTokens()

		setTimeout(() => {
			const inputToken = tokenInfo(inputTokenAddress)
			const outputToken = tokenInfo(outputTokenAddress)

			handleInputSelect(outputToken)
			handleOutputSelect(inputToken)
		}, 250)
	}

	useEffect(() => {
		// console.log('AmountIn', inputAmount)
		// console.log(currencies)

		if (inputAmount) {
			const value = parseFloat(inputAmount)
			const inputToken = tokenInfo(inputTokenAddress)
			const outputToken = tokenInfo(outputTokenAddress)
			let feeRate = 0
			let rewardRate = 0
			let adjustedWrapRatio = wrapRatio

			if (
				inputToken &&
				outputToken &&
				(inputToken.tokenInfo.isWSafeToken || outputToken.tokenInfo.isWSafeToken)
			) {
				if (inputToken.tokenInfo.isWSafeToken) {
					feeRate = inputToken.tokenInfo.safeFee + inputToken.tokenInfo.unwrapFee
				} else {
					feeRate = outputToken.tokenInfo.safeFee + outputToken.tokenInfo.wrapFee
					rewardRate = outputToken.tokenInfo.safeFee
					adjustedWrapRatio = 1 / adjustedWrapRatio
				}
			}

			const newOutputAmount = value * (1 - feeRate) * adjustedWrapRatio
			const parsedOutputAmount = parseFloat(outputAmount || '0')

			if (Math.abs(parsedOutputAmount - newOutputAmount) / parsedOutputAmount > 0.0001) {
				setOutputAmount((+newOutputAmount.toFixed(4)).toString())
			}

			// console.log(value * rewardRate, value, rewardRate)

			setOutputPairAmount((+(value * rewardRate).toFixed(4)).toString())
			setInputPairAmount('')
		} else {
			setOutputAmount('')
			setOutputPairAmount('')
			setInputPairAmount('')
		}
	}, [inputAmount, inputTokenAddress, outputTokenAddress])

	useEffect(() => {
		// console.log('AmountOut', outputAmount)
		// console.log(currencies)

		if (outputAmount) {
			const value = parseFloat(outputAmount)
			const inputToken = tokenInfo(inputTokenAddress)
			const outputToken = tokenInfo(outputTokenAddress)
			let feeRate = 0
			let rewardRate = 0
			let adjustedWrapRatio = wrapRatio

			if (
				inputToken &&
				outputToken &&
				(inputToken.tokenInfo.isWSafeToken || outputToken.tokenInfo.isWSafeToken)
			) {
				if (inputToken.tokenInfo.isWSafeToken) {
					feeRate = inputToken.tokenInfo.safeFee + inputToken.tokenInfo.unwrapFee
				} else {
					feeRate = outputToken.tokenInfo.safeFee + outputToken.tokenInfo.wrapFee
					rewardRate = outputToken.tokenInfo.safeFee
					adjustedWrapRatio = 1 / adjustedWrapRatio
				}
			}

			const newInputAmount = value / (1 - feeRate) / adjustedWrapRatio
			const parsedInputAmount = parseFloat(inputAmount || '0')

			if (Math.abs(parsedInputAmount - newInputAmount) / parsedInputAmount > 0.0001) {
				setInputAmount((+newInputAmount.toFixed(4)).toString())
			}

			setOutputPairAmount((+(newInputAmount * rewardRate).toFixed(4)).toString())
			setInputPairAmount('')
		} else {
			setInputAmount('')
			setOutputPairAmount('')
			setInputPairAmount('')
		}
	}, [outputAmount, inputTokenAddress, outputTokenAddress])

	const handleTypeInput = useCallback(
		(value: string) => {
			// Bypass normal swap procedure
			// onUserInput(Field.INPUT, value)

			value = value.replace(/[^0-9.]/g, '')
			setInputAmount(value)
		},
		[onUserInput],
	)
	const handleTypeOutput = useCallback(
		(value: string) => {
			// Bypass normal swap procedure
			// onUserInput(Field.OUTPUT, value)

			value = value.replace(/[^0-9.]/g, '')
			setOutputAmount(value)
		},
		[onUserInput],
	)

	// modal and loading
	const [
		{ showConfirm, tradeToConfirm, swapErrorMessage, attemptingTxn, txHash },
		setSwapState,
	] = useState<{
		showConfirm: boolean
		tradeToConfirm: Trade | undefined
		attemptingTxn: boolean
		swapErrorMessage: string | undefined
		txHash: string | undefined
	}>({
		showConfirm: false,
		tradeToConfirm: undefined,
		attemptingTxn: false,
		swapErrorMessage: undefined,
		txHash: undefined,
	})

	const formattedAmounts = {
		[independentField]: typedValue,
		[dependentField]: showWrap
			? parsedAmounts[independentField]?.toExact() ?? ''
			: parsedAmounts[dependentField]?.toSignificant(6) ?? '',
	}

	const route = trade?.route
	const userHasSpecifiedInputOutput = Boolean(
		currencies[Field.INPUT] &&
			currencies[Field.OUTPUT] &&
			parsedAmounts[independentField]?.greaterThan(JSBI.BigInt(0)),
	)
	// const noRoute = !route
	const noRoute = true // Force remove swap route UI

	let divAmount = '1'
	if (currencies.INPUT) {
		for (let i = 0; i < 18 - currencies.INPUT.decimals; i++) {
			divAmount += '0'
		}
	}

	// check whether the user has approved the router on the input token
	const [approval, approveCallback] = useApproveCallback(
		new TokenAmount(
			currencies.INPUT instanceof Token
				? (currencies.INPUT as Token)
				: new Token(chainId, SAFEMOON_TOKEN_ADDRESS[chainId], 9, 'SAFEMOON', 'Safemoon'),
			BigNumber.from(ethers.utils.parseEther(inputAmount || '0'))
				.div(divAmount)
				.toString(),
		),
		outputTokenAddress || SAFEMOON_TOKEN_ADDRESS[chainId],
	)

	// console.log(approval)

	// check if user has gone through approval process, used to show two step buttons, reset on token change
	const [approvalSubmitted, setApprovalSubmitted] = useState<boolean>(false)

	// mark when a user has submitted an approval, reset onTokenSelection for input field
	useEffect(() => {
		if (approval === ApprovalState.PENDING) {
			setApprovalSubmitted(true)
		}
	}, [approval, approvalSubmitted])

	const maxAmountInput: CurrencyAmount | undefined = maxAmountSpend(currencyBalances[Field.INPUT])
	const atMaxAmountInput = Boolean(
		maxAmountInput && parsedAmounts[Field.INPUT]?.equalTo(maxAmountInput),
	)

	// the callback to execute the swap
	const { callback: swapCallback, error: swapCallbackError } = useSwapCallback(
		trade,
		allowedSlippage,
		deadline,
		recipient,
	)

	const { priceImpactWithoutFee } = computeTradePriceBreakdown(trade)

	const handleSwap = useCallback(() => {
		if (priceImpactWithoutFee && !confirmPriceImpactWithoutFee(priceImpactWithoutFee)) {
			return
		}
		if (!swapCallback) {
			return
		}
		setSwapState((prevState) => ({
			...prevState,
			attemptingTxn: true,
			swapErrorMessage: undefined,
			txHash: undefined,
		}))
		swapCallback()
			.then((hash) => {
				setSwapState((prevState) => ({
					...prevState,
					attemptingTxn: false,
					swapErrorMessage: undefined,
					txHash: hash,
				}))
			})
			.catch((error) => {
				setSwapState((prevState) => ({
					...prevState,
					attemptingTxn: false,
					swapErrorMessage: error.message,
					txHash: undefined,
				}))
			})
	}, [priceImpactWithoutFee, swapCallback, setSwapState])

	// errors
	const [showInverted, setShowInverted] = useState<boolean>(false)

	// warnings on slippage
	const priceImpactSeverity = warningSeverity(priceImpactWithoutFee)

	// show approve flow when: no error on inputs, not approved or pending, or approved in current session
	// never show if price impact is above threshold in non expert mode
	const showApproveFlow =
		tokenInfo(outputTokenAddress) &&
		tokenInfo(outputTokenAddress).tokenInfo.isWSafeToken &&
		((inputAmount && approval === ApprovalState.NOT_APPROVED) ||
			approval === ApprovalState.PENDING ||
			(approvalSubmitted &&
				approval === ApprovalState.APPROVED &&
				!(priceImpactSeverity > 3 && !isExpertMode)))

	const handleConfirmDismiss = useCallback(() => {
		setSwapState((prevState) => ({ ...prevState, showConfirm: false }))

		// if there was a tx hash, we want to clear the input
		if (txHash) {
			onUserInput(Field.INPUT, '')
		}
	}, [onUserInput, txHash, setSwapState])

	const handleAcceptChanges = useCallback(() => {
		setSwapState((prevState) => ({ ...prevState, tradeToConfirm: trade }))
	}, [trade])

	// This will check to see if the user has selected Syrup or SafeMoon to either buy or sell.
	// If so, they will be alerted with a warning message.
	const checkForWarning = useCallback(
		(selected: string, purchaseType: string) => {
			if (['SYRUP', 'SAFEMOON'].includes(selected)) {
				setTransactionWarning({
					selectedToken: selected,
					purchaseType,
				})
			}
		},
		[setTransactionWarning],
	)

	const [rewardToken, setRewardToken] = useState<WrappedTokenInfo | null>()

	const handleInputSelect = useCallback(
		(inputCurrency) => {
			function onCurrencySelectionWrapper(field, currency) {
				onCurrencySelection(field, currency)

				if (field === Field.INPUT) {
					setInputTokenAddress(currency.address)
				} else {
					setOutputTokenAddress(currency.address)
				}
			}

			setInputAmount('')
			setOutputAmount('')
			setOutputPairAmount('')
			setInputPairAmount('')

			setApprovalSubmitted(false) // reset 2 step UI for approvals
			onCurrencySelectionWrapper(Field.INPUT, inputCurrency)
			if (inputCurrency.symbol === 'SYRUP') {
				checkForWarning(inputCurrency.symbol, 'Selling')
			}
			// if (inputCurrency.symbol === 'SAFEMOON') {
			// 	checkForWarning(inputCurrency.symbol, 'Selling')
			// }

			// console.log('INPUT CHANGE', inputCurrency)

			if (inputCurrency.tokenInfo) {
				if (inputCurrency.tokenInfo.isSafeToken) {
					onCurrencySelectionWrapper(Field.OUTPUT, Safe2WSafe(inputCurrency.address))
					setRewardToken(Safe2RSafe(inputCurrency.address))
				} else if (inputCurrency.tokenInfo.isWSafeToken) {
					onCurrencySelectionWrapper(Field.OUTPUT, WSafe2Safe(inputCurrency.address))
					setRewardToken(null)
				}
			}
		},
		[onCurrencySelection, setApprovalSubmitted, checkForWarning],
	)

	const handleMaxInput = useCallback(() => {
		if (maxAmountInput) {
			// onUserInput(Field.INPUT, maxAmountInput.toExact())
			handleTypeInput(maxAmountInput.toExact())
		}
	}, [maxAmountInput, onUserInput])

	const handleOutputSelect = useCallback(
		(outputCurrency) => {
			function onCurrencySelectionWrapper(field, currency) {
				onCurrencySelection(field, currency)

				if (field === Field.INPUT) {
					setInputTokenAddress(currency.address)
				} else {
					setOutputTokenAddress(currency.address)
				}
			}

			setInputAmount('')
			setOutputAmount('')
			setOutputPairAmount('')
			setInputPairAmount('')

			onCurrencySelectionWrapper(Field.OUTPUT, outputCurrency)
			if (outputCurrency.symbol === 'SYRUP') {
				checkForWarning(outputCurrency.symbol, 'Buying')
			}
			// if (outputCurrency.symbol === 'SAFEMOON') {
			// 	checkForWarning(outputCurrency.symbol, 'Buying')
			// }

			// console.log('OUTPUT CHANGE', outputCurrency)

			if (outputCurrency.tokenInfo) {
				if (outputCurrency.tokenInfo.isSafeToken) {
					onCurrencySelectionWrapper(Field.INPUT, Safe2WSafe(outputCurrency.address))
					setRewardToken(null)
				} else if (outputCurrency.tokenInfo.isWSafeToken) {
					onCurrencySelectionWrapper(Field.INPUT, WSafe2Safe(outputCurrency.address))
					setRewardToken(WSafe2RSafe(outputCurrency.address))
				}
			}
		},
		[onCurrencySelection, checkForWarning],
	)

	useEffect(() => {
		setTimeout(() => {
			handleInputSelect(tokenInfo(SAFEMOON_TOKEN_ADDRESS[chainId]))
		}, 250)
	}, [])

	return (
		<Container>
			<TokenWarningModal
				isOpen={urlLoadedTokens.length > 0 && !dismissTokenWarning}
				tokens={urlLoadedTokens}
				onConfirm={handleConfirmTokenWarning}
			/>
			<SyrupWarningModal
				isOpen={transactionWarning.selectedToken === 'SYRUP'}
				transactionType={transactionWarning.purchaseType}
				onConfirm={handleConfirmWarning}
			/>
			<RSafeWarningModal
				isOpen={rSafeTokenWarning}
				onConfirm={handleConfirmRSafeTokenWarning}
			/>
			<CardNav activeIndex={2} />
			<AppBody>
				<Wrapper id="swap-page">
					<ConfirmSwapModal
						isOpen={showConfirm}
						trade={trade}
						originalTrade={tradeToConfirm}
						onAcceptChanges={handleAcceptChanges}
						attemptingTxn={attemptingTxn}
						txHash={txHash}
						recipient={recipient}
						allowedSlippage={allowedSlippage}
						onConfirm={handleSwap}
						swapErrorMessage={swapErrorMessage}
						onDismiss={handleConfirmDismiss}
					/>
					<PageHeader
						title={TranslateString(8, 'Wrap')}
						description={TranslateString(1192, 'Wrap your Safe to wSafe')}
					/>
					<CardBody>
						<AutoColumn gap="md">
							<CurrencyInputPanel
								label={
									independentField === Field.OUTPUT && !showWrap && trade
										? TranslateString(194, 'From (estimated)')
										: TranslateString(76, 'From')
								}
								value={inputAmount}
								pairValue={inputPairAmount}
								showMaxButton={!atMaxAmountInput}
								currency={currencies[Field.INPUT]}
								onUserInput={handleTypeInput}
								onMax={handleMaxInput}
								onCurrencySelect={handleInputSelect}
								otherCurrency={currencies[Field.OUTPUT]}
								tokenMode="safe"
								id="swap-currency-input"
							/>
							<AutoColumn justify="space-between">
								<AutoRow
									justify={isExpertMode ? 'space-between' : 'center'}
									style={{ padding: '0 1rem' }}
								>
									<ArrowWrapper clickable>
										<IconButton
											variant="tertiary"
											onClick={() => {
												setApprovalSubmitted(false) // reset 2 step UI for approvals
												onSwitchTokensWrapped()
											}}
											style={{ borderRadius: '50%' }}
											scale="sm"
										>
											<ArrowDownIcon color="primary" width="24px" />
										</IconButton>
									</ArrowWrapper>
									{recipient === null && !showWrap && isExpertMode ? (
										<LinkStyledButton
											id="add-recipient-button"
											onClick={() => onChangeRecipient('')}
										>
											+ Add a send (optional)
										</LinkStyledButton>
									) : null}
								</AutoRow>
							</AutoColumn>
							<CurrencyInputPanel
								value={outputAmount}
								pairValue={outputPairAmount}
								onUserInput={handleTypeOutput}
								label={
									independentField === Field.INPUT && !showWrap && trade
										? TranslateString(196, 'To (estimated)')
										: TranslateString(80, 'To')
								}
								showMaxButton={false}
								currency={currencies[Field.OUTPUT]}
								onCurrencySelect={handleOutputSelect}
								otherCurrency={currencies[Field.INPUT]}
								pairCurrency={rewardToken}
								tokenMode="safe"
								id="swap-currency-output"
							/>

							{recipient !== null && !showWrap ? (
								<>
									<AutoRow justify="space-between" style={{ padding: '0 1rem' }}>
										<ArrowWrapper clickable={false}>
											<ArrowDown size="16" color={theme.colors.textSubtle} />
										</ArrowWrapper>
										<LinkStyledButton
											id="remove-recipient-button"
											onClick={() => onChangeRecipient(null)}
										>
											- Remove send
										</LinkStyledButton>
									</AutoRow>
									<AddressInputPanel
										id="recipient"
										value={recipient}
										onChange={onChangeRecipient}
									/>
								</>
							) : null}

							{showWrap ? null : (
								<Card padding=".25rem .75rem 0 .75rem" borderRadius="20px">
									<AutoColumn gap="4px">
										{Boolean(trade) && (
											<RowBetween align="center">
												<Text fontSize="14px">
													{TranslateString(1182, 'Price')}
												</Text>
												<TradePrice
													price={trade?.executionPrice}
													showInverted={showInverted}
													setShowInverted={setShowInverted}
												/>
											</RowBetween>
										)}
										{allowedSlippage !== INITIAL_ALLOWED_SLIPPAGE && (
											<RowBetween align="center">
												<Text fontSize="14px">
													{TranslateString(88, 'Slippage Tolerance')}
												</Text>
												<Text fontSize="14px">
													{allowedSlippage / 100}%
												</Text>
											</RowBetween>
										)}
									</AutoColumn>
								</Card>
							)}
						</AutoColumn>
						<BottomGrouping>
							{!account ? (
								<ConnectWalletButton width="100%" />
							) : showWrap ? (
								showApproveFlow ? (
									<RowBetween>
										<Button
											onClick={approveCallback}
											disabled={
												approval !== ApprovalState.NOT_APPROVED ||
												approvalSubmitted
											}
											style={{ width: '100%' }}
											variant={
												approval === ApprovalState.APPROVED
													? 'success'
													: 'primary'
											}
										>
											{approval === ApprovalState.PENDING ? (
												<AutoRow gap="6px" justify="center">
													Approving <Loader stroke="white" />
												</AutoRow>
											) : approvalSubmitted &&
											  approval === ApprovalState.APPROVED ? (
												'Approved'
											) : (
												`Approve ${currencies[Field.INPUT]?.symbol}`
											)}
										</Button>
									</RowBetween>
								) : (
									<Button
										disabled={Boolean(wrapInputError)}
										onClick={onWrap}
										width="100%"
									>
										{wrapInputError ??
											(wrapType === WrapType.WRAP
												? 'Wrap'
												: wrapType === WrapType.UNWRAP
												? 'Unwrap'
												: null)}
									</Button>
								)
							) : noRoute && userHasSpecifiedInputOutput ? (
								<GreyCard style={{ textAlign: 'center' }}>
									<Text mb="4px">
										{TranslateString(
											1194,
											'Insufficient liquidity for this trade.',
										)}
									</Text>
								</GreyCard>
							) : showApproveFlow ? (
								<RowBetween>
									<Button
										onClick={approveCallback}
										disabled={
											approval !== ApprovalState.NOT_APPROVED ||
											approvalSubmitted
										}
										style={{ width: '48%' }}
										variant={
											approval === ApprovalState.APPROVED
												? 'success'
												: 'primary'
										}
									>
										{approval === ApprovalState.PENDING ? (
											<AutoRow gap="6px" justify="center">
												Approving <Loader stroke="white" />
											</AutoRow>
										) : approvalSubmitted &&
										  approval === ApprovalState.APPROVED ? (
											'Approved'
										) : (
											`Approve ${currencies[Field.INPUT]?.symbol}`
										)}
									</Button>
									<Button
										onClick={() => {
											if (isExpertMode) {
												handleSwap()
											} else {
												setSwapState({
													tradeToConfirm: trade,
													attemptingTxn: false,
													swapErrorMessage: undefined,
													showConfirm: true,
													txHash: undefined,
												})
											}
										}}
										style={{ width: '48%' }}
										id="swap-button"
										disabled={
											!isValid ||
											approval !== ApprovalState.APPROVED ||
											(priceImpactSeverity > 3 && !isExpertMode)
										}
										variant={
											isValid && priceImpactSeverity > 2
												? 'danger'
												: 'primary'
										}
									>
										{priceImpactSeverity > 3 && !isExpertMode
											? `Price Impact High`
											: `Swap${priceImpactSeverity > 2 ? ' Anyway' : ''}`}
									</Button>
								</RowBetween>
							) : (
								<Button
									onClick={() => {
										if (isExpertMode) {
											handleSwap()
										} else {
											setSwapState({
												tradeToConfirm: trade,
												attemptingTxn: false,
												swapErrorMessage: undefined,
												showConfirm: true,
												txHash: undefined,
											})
										}
									}}
									id="swap-button"
									disabled={
										!isValid ||
										(priceImpactSeverity > 3 && !isExpertMode) ||
										!!swapCallbackError
									}
									variant={
										isValid && priceImpactSeverity > 2 && !swapCallbackError
											? 'danger'
											: 'primary'
									}
									width="100%"
								>
									{swapInputError ||
										(priceImpactSeverity > 3 && !isExpertMode
											? `Price Impact Too High`
											: `Swap${priceImpactSeverity > 2 ? ' Anyway' : ''}`)}
								</Button>
							)}
							{/* {showApproveFlow && <ProgressSteps steps={[approval === ApprovalState.APPROVED]} />} */}
							{isExpertMode && swapErrorMessage ? (
								<SwapCallbackError error={swapErrorMessage} />
							) : null}
						</BottomGrouping>
					</CardBody>
					{/* <img
						src="/images/food1.svg"
						alt="Food1"
						className="_pst-asl _l-0px _bt-0px"
						style={{ transform: 'translate3d(-40%, 20px, 0) rotate(15deg)' }}
						width="120"
					/>
					<img
						src="/images/food2.svg"
						alt="Food1"
						className="_pst-asl _r-0px _bt-0px"
						style={{ transform: 'translate3d(65%, 30px, 0) rotate(15deg)' }}
						width="60"
					/> */}
					{/* <img src="/images/food2.svg" alt="Food1" />
					<img src="/images/food5.svg" alt="Food1" /> */}
				</Wrapper>
			</AppBody>
			<AdvancedSwapDetailsDropdown trade={trade} />
		</Container>
	)
}

export default Wrap
